import React, { useCallback, useEffect, useRef, useState } from "react";

// Два ОБЯЗАТЕЛЬНЫХ прохода на одно фото: модератор обрезает один файл дважды,
// бэкенд получает оба кадра. Пропорции и подписи фиксированные — иначе каждая
// история будет сверстана по-своему.
//
// Шаг 1 — «телефон»: кадр на всю высоту экрана, пропорция 9:16. Это основная
//          фотография в карусели открытой истории.
// Шаг 2 — «карточка»: горизонтальная обложка 327×138 в списке историй.
const STEPS = [
	{
		key: "phone",
		label: "На экран телефона",
		hint: "Вертикальный кадр 9:16 — он показывается в карусели открытой истории на всю высоту экрана.",
		aspect: 9 / 16,
	},
	{
		key: "card",
		label: "Для карточки",
		hint: "Горизонтальная обложка 327×200 — она видна в списке историй. Пропорцию поднял владелец, чтобы лица были заметнее.",
		aspect: 327 / 200,
	},
];

// Больше не увеличиваем: апскейл ничего не добавляет, только вес.
const MAX_OUT = 2000;
const MIN_CROP = 40;

const CORNERS = ["nw", "ne", "sw", "se"];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PhotoCropper = ({
	file,
	position,
	total,
	onSkip,
	onCancelAll,
	onConfirm,
	onUploadOriginal,
}) => {
	const [src, setSrc] = useState(null);
	const [natural, setNatural] = useState(null);
	const [view, setView] = useState(0);
	const [rect, setRect] = useState(null);
	const [stepIndex, setStepIndex] = useState(0);
	const [firstCropBase64, setFirstCropBase64] = useState(null);
	const [isBusy, setIsBusy] = useState(false);

	const imgRef = useRef(null);
	const dragRef = useRef(null);

	const step = STEPS[stepIndex];
	const aspect = step.aspect;

	useEffect(() => {
		const url = URL.createObjectURL(file);
		setSrc(url);
		setNatural(null);
		setRect(null);
		setStepIndex(0);
		setFirstCropBase64(null);

		return () => URL.revokeObjectURL(url);
	}, [file]);

	const measure = useCallback(() => {
		if (imgRef.current) setView(imgRef.current.clientWidth);
	}, []);

	useEffect(() => {
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, [measure]);

	/** Наибольшая рамка заданной пропорции, влезающая в картинку. */
	const fit = (w, h, ratio) => {
		if (!ratio) return { w, h };

		let cw = w;
		let ch = cw / ratio;
		if (ch > h) {
			ch = h;
			cw = ch * ratio;
		}
		return { w: cw, h: ch };
	};

	const applyStepAspect = useCallback(
		(nextAspect) => {
			if (!natural) return;

			const size = fit(natural.w, natural.h, nextAspect);
			// Лица почти всегда в верхней половине кадра, поэтому стартуем выше
			// центра: так реже приходится двигать рамку вручную.
			setRect({
				x: (natural.w - size.w) / 2,
				y: clamp((natural.h - size.h) * 0.25, 0, natural.h - size.h),
				w: size.w,
				h: size.h,
			});
		},
		[natural],
	);

	const onImageLoad = (event) => {
		const w = event.target.naturalWidth;
		const h = event.target.naturalHeight;
		setNatural({ w, h });

		const size = fit(w, h, aspect);
		setRect({
			x: (w - size.w) / 2,
			y: clamp((h - size.h) * 0.25, 0, h - size.h),
			w: size.w,
			h: size.h,
		});

		measure();
	};

	// Между шагами меняется пропорция; рамка нового шага сбрасывается на центр
	// (у кадров сильно разная ориентация — сохранять координаты бессмысленно).
	useEffect(() => {
		if (!natural) return;
		applyStepAspect(aspect);
	}, [stepIndex, natural, applyStepAspect, aspect]);

	const scale = natural && view ? view / natural.w : 0;

	const startDrag = (event, corner) => {
		if (!rect) return;

		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		dragRef.current = { px: event.clientX, py: event.clientY, rect, corner };
	};

	const onPointerMove = (event) => {
		const drag = dragRef.current;
		if (!drag || !natural || !scale) return;

		const dx = (event.clientX - drag.px) / scale;
		const dy = (event.clientY - drag.py) / scale;
		const s = drag.rect;

		if (!drag.corner) {
			setRect({
				...s,
				x: clamp(s.x + dx, 0, natural.w - s.w),
				y: clamp(s.y + dy, 0, natural.h - s.h),
			});
			return;
		}

		let { x, y, w, h } = s;
		const west = drag.corner.includes("w");
		const north = drag.corner.includes("n");

		if (west) {
			x = s.x + dx;
			w = s.w - dx;
		} else {
			w = s.w + dx;
		}

		if (north) {
			y = s.y + dy;
			h = s.h - dy;
		} else {
			h = s.h + dy;
		}

		if (aspect) {
			// При закреплённой пропорции ведущей берём ширину, а высоту
			// подгоняем, удерживая противоположный угол на месте.
			h = w / aspect;
			if (north) y = s.y + s.h - h;
		}

		w = clamp(w, MIN_CROP, natural.w);
		h = clamp(h, MIN_CROP, natural.h);

		if (aspect) {
			h = w / aspect;
			if (h > natural.h) {
				h = natural.h;
				w = h * aspect;
			}
			if (north) y = s.y + s.h - h;
			if (west) x = s.x + s.w - w;
		}

		setRect({
			w,
			h,
			x: clamp(x, 0, natural.w - w),
			y: clamp(y, 0, natural.h - h),
		});
	};

	const onPointerUp = () => {
		dragRef.current = null;
	};

	const onZoom = (event) => {
		const w = Number(event.target.value);
		const h = aspect ? w / aspect : w * (rect.h / rect.w);
		const cx = rect.x + rect.w / 2;
		const cy = rect.y + rect.h / 2;

		setRect({
			w,
			h,
			x: clamp(cx - w / 2, 0, natural.w - w),
			y: clamp(cy - h / 2, 0, natural.h - h),
		});
	};

	/** Снимает текущий кадр в base64 без префикса data:image/… */
	const snapshotBase64 = () => {
		if (!rect || !imgRef.current) return null;

		const outW = Math.round(Math.min(rect.w, MAX_OUT));
		const canvas = document.createElement("canvas");
		canvas.width = outW;
		canvas.height = Math.max(1, Math.round((outW * rect.h) / rect.w));

		const ctx = canvas.getContext("2d");
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(
			imgRef.current,
			rect.x, rect.y, rect.w, rect.h,
			0, 0, canvas.width, canvas.height,
		);

		const url = canvas.toDataURL("image/jpeg", 0.92);
		return url.slice(url.indexOf(",") + 1);
	};

	const confirmStep = () => {
		setIsBusy(true);
		try {
			const base64 = snapshotBase64();
			if (!base64) return;

			if (stepIndex === 0) {
				setFirstCropBase64(base64);
				setStepIndex(1);
			} else {
				// Второй шаг — отправка. onConfirm(phoneBase64, cardBase64).
				onConfirm(firstCropBase64, base64);
			}
		} finally {
			setIsBusy(false);
		}
	};

	const back = () => {
		if (stepIndex === 0) return;
		setStepIndex(0);
		// Первый кадр не теряем: при повторном подтверждении шага 1 он просто
		// перезапишется, но модератор может и вернуться на шаг 2 без изменений.
	};

	const maxW = natural ? fit(natural.w, natural.h, aspect).w : 0;

	return (
		<div className="fscrop">
			<div className="fscrop__box">
				<div className="fscrop__head">
					<strong>
						Кадрирование — шаг {stepIndex + 1} из {STEPS.length}: {step.label}
					</strong>
					<span>
						{file.name} · {position} из {total}
					</span>
				</div>

				<p className="fscrop__hint">{step.hint}</p>

				<div className="fscrop__stage">
					<img
						ref={imgRef}
						src={src}
						alt=""
						className="fscrop__image"
						onLoad={onImageLoad}
						draggable={false}
					/>

					{rect && scale > 0 && (
						<div
							className="fscrop__frame"
							style={{
								left: rect.x * scale,
								top: rect.y * scale,
								width: rect.w * scale,
								height: rect.h * scale,
							}}
							onPointerDown={(e) => startDrag(e, null)}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerCancel={onPointerUp}
						>
							{CORNERS.map((corner) => (
								<span
									key={corner}
									className={`fscrop__handle is-${corner}`}
									onPointerDown={(e) => startDrag(e, corner)}
									onPointerMove={onPointerMove}
									onPointerUp={onPointerUp}
									onPointerCancel={onPointerUp}
								/>
							))}
						</div>
					)}
				</div>

				<p className="fscrop__hint">
					Рамку можно перетаскивать, углы — тянуть.
					{rect && (
						<>
							{" "}
							Кадр: {Math.round(rect.w)}×{Math.round(rect.h)}
						</>
					)}
				</p>

				{rect && (
					<label className="fscrop__zoom">
						Масштаб
						<input
							type="range"
							min={MIN_CROP}
							max={Math.round(maxW)}
							value={Math.round(Math.min(rect.w, maxW))}
							onChange={onZoom}
						/>
					</label>
				)}

				<div className="fscrop__actions">
					{stepIndex > 0 && (
						<button type="button" onClick={back} disabled={isBusy}>
							Назад к шагу 1
						</button>
					)}
					<button type="button" onClick={confirmStep} disabled={!rect || isBusy}>
						{stepIndex === STEPS.length - 1
							? "Обрезать и загрузить"
							: "Далее"}
					</button>
					<button type="button" onClick={onUploadOriginal} disabled={isBusy}>
						Загрузить как есть
					</button>
					<button type="button" onClick={onSkip} disabled={isBusy}>
						Пропустить
					</button>
					<button
						type="button"
						className="fscrop__cancel"
						onClick={onCancelAll}
						disabled={isBusy}
					>
						Отменить всё
					</button>
				</div>
			</div>
		</div>
	);
};

export default PhotoCropper;
